const MODULE_PERMISSIONS = [
  "dashboard",
  "drivers",
  "vehicles",
  "vehicle_documents",
  "live_tracking",
  "branch_overview",
  "suppliers",
  "assignments",
  "reports",
  "settings",
  "master_data",
  "auditlog",
  "recycle_bin",
  "staff_management",
  "messages",
  "followups",
  "outstanding_view",
  "outstanding_update",
];

const ACCESS_LEVELS = ["view", "edit", "delete"];

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const normalizeAccessLevels = (value) => {
  let values = [];

  if (Array.isArray(value)) {
    values = value;
  } else if (typeof value === "string") {
    values = value.split(",");
  }

  const unique = [...new Set(
    values
      .map((item) => String(item || "").trim().toLowerCase())
      .filter((item) => ACCESS_LEVELS.includes(item))
  )];

  return unique;
};

const getStoredAccessLevels = () => {
  const permissions = getPermissions();

  const stored =
    localStorage.getItem("permission_levels") ||
    permissions.permission_levels ||
    permissions.permission_level ||
    "view";

  const levels = normalizeAccessLevels(stored);

  return levels.length ? levels : ["view"];
};

export const getRoleName = () =>
  localStorage.getItem("role_name") ||
  localStorage.getItem("role") ||
  localStorage.getItem("user_role") ||
  "";

export const isSuperadmin = () => {
  const role = normalizeRole(getRoleName());
  return role === "superadmin" || role === "super admin";
};

export const isAdmin = () => normalizeRole(getRoleName()) === "admin";

export const isStaff = () => normalizeRole(getRoleName()) === "staff";

export const getPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions") || "{}";
    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      return {};
    }

    return parsed;
  } catch {
    return {};
  }
};

export const getPermissionLevels = () => {
  if (isSuperadmin()) {
    return [...ACCESS_LEVELS];
  }

  return getStoredAccessLevels();
};

export const getPermissionLevel = () => {
  const levels = getPermissionLevels();

  if (levels.includes("delete")) return "delete";
  if (levels.includes("edit")) return "edit";
  return "view";
};

export const setPermissionLevels = (levels) => {
  const normalized = normalizeAccessLevels(levels);
  const safeLevels = normalized.length ? normalized : ["view"];
  const value = safeLevels.join(",");

  localStorage.setItem("permission_levels", value);
  localStorage.setItem("permission_level", value);

  const permissions = getPermissions();
  permissions.permission_levels = safeLevels;
  permissions.permission_level = value;

  localStorage.setItem("permissions", JSON.stringify(permissions));

  return safeLevels;
};

export const setPermissionLevel = (level) => {
  return setPermissionLevels(level);
};

export const hasPermission = (module, action = "view") => {
  const moduleName = String(module || "").trim();
  const actionName = String(action || "view").trim().toLowerCase();

  if (!moduleName) return false;

  if (isSuperadmin()) return true;

  if (!ACCESS_LEVELS.includes(actionName)) {
    return false;
  }

  const permissions = getPermissions();

  if (permissions[moduleName] !== true) {
    return false;
  }

  const selectedLevels = getPermissionLevels();

  return selectedLevels.includes(actionName);
};

export const hasViewPermission = (module) =>
  hasPermission(module, "view");

export const hasEditPermission = (module) =>
  hasPermission(module, "edit");

export const hasDeletePermission = (module) =>
  hasPermission(module, "delete");

export const hasAnyPermission = (modules = [], action = "view") =>
  isSuperadmin() ||
  modules.some((module) => hasPermission(module, action));

export const hasAllPermissions = (modules = [], action = "view") =>
  isSuperadmin() ||
  modules.every((module) => hasPermission(module, action));

export const getAccessibleModules = () =>
  isSuperadmin()
    ? [...MODULE_PERMISSIONS]
    : MODULE_PERMISSIONS.filter((module) =>
        hasPermission(module, "view")
      );

export const canManageStaff = () =>
  isSuperadmin() ||
  hasPermission("staff_management", "view");

export const canEditStaff = () =>
  isSuperadmin() ||
  hasPermission("staff_management", "edit");

export const canDeleteStaff = () =>
  isSuperadmin() ||
  hasPermission("staff_management", "delete");

export const getModulePermissions = () => [...MODULE_PERMISSIONS];

const permissionsApi = {
  getRoleName,
  isSuperadmin,
  isAdmin,
  isStaff,
  getPermissions,
  getPermissionLevels,
  getPermissionLevel,
  setPermissionLevels,
  setPermissionLevel,
  hasPermission,
  hasViewPermission,
  hasEditPermission,
  hasDeletePermission,
  hasAnyPermission,
  hasAllPermissions,
  getAccessibleModules,
  getModulePermissions,
  canManageStaff,
  canEditStaff,
  canDeleteStaff,
};

export default permissionsApi;
